// Links session uuids in agent prose to the same Workspace navigation a result-
// table cell performs. Only COMPLETE uuids the index recognises as sessions;
// anything else stays as the model wrote it. See proseSessionIndex.js.

import { Fragment, useMemo, useRef } from "react";
import { createSessionIdIndex, ingestChatSources } from "../lib/proseSessionIndex.js";
import { forEachResult } from "../lib/toolResultStore.js";

// No lookbehind - Safari before 16.4 lacks it; boundaries are checked against
// the neighbouring characters so a uuid inside a longer hex run is rejected.
const UUID_SCAN = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
const HEX_OR_DASH = /[0-9a-fA-F-]/;
const UUID_LEN = 36;

// A button, not an anchor: a scope has no stable url - the destination depends
// on which tabs are already open, so there is no href to put in the attribute.
// (A trace does have one, which is why TraceLanesView uses a real anchor.)
const LINK_CLASS =
    "cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[length:inherit] " +
    "leading-[inherit] text-[color:inherit] underline decoration-dotted " +
    "decoration-[color:var(--line-strong)] underline-offset-[2px] " +
    "hover:text-[color:var(--green)] hover:decoration-solid " +
    "hover:decoration-[color:var(--green)]";

// The `linkifyText` hook Markdown consumes. ChatConsole passes its
// `openScopeAsTab` as `openScope`.
export function makeSessionLinkifier({ index, openScope }) {
    return function linkifyText({ text, key }) {
        // Cheapest rejects first: this runs per prose run, per streaming delta.
        if (!text || text.length < UUID_LEN || !index || index.size === 0) return null;

        let out = null;
        let cursor = 0;
        UUID_SCAN.lastIndex = 0;
        let m;
        while ((m = UUID_SCAN.exec(text)) !== null) {
            const before = text[m.index - 1];
            const after = text[m.index + UUID_LEN];
            // Part of a longer hex blob.
            if ((before && HEX_OR_DASH.test(before)) || (after && HEX_OR_DASH.test(after))) continue;
            const canonical = index.resolve(m[0]);
            if (!canonical) continue;
            out ||= [];
            if (m.index > cursor) out.push(text.slice(cursor, m.index));
            out.push(
                <button
                    key={`${key}:${m.index}`}
                    type="button"
                    onClick={() => openScope("sessions", [canonical])}
                    title={`Traces · ${canonical}`}
                    aria-label={`Open trace ${canonical} in Investigations`}
                    className={LINK_CLASS}
                >
                    {m[0]}
                </button>,
            );
            cursor = m.index + m[0].length;
        }
        if (!out) return null;
        if (cursor < text.length) out.push(text.slice(cursor));
        return <Fragment key={key}>{out}</Fragment>;
    };
}

// Per-chat index, so an id seen in one chat never linkifies in another.
// Ingestion runs during render on purpose: the matcher must match the
// transcript React is about to paint, or Markdown's memo serves a stale one.
// Idempotent, so StrictMode's double invoke is a no-op.
export function useSessionLinkifier({ chatId, items, openScope }) {
    const perChat = useRef(new Map());
    const key = chatId == null ? "" : String(chatId);

    let slot = perChat.current.get(key);
    if (!slot) {
        slot = { index: createSessionIdIndex(), seen: new WeakSet() };
        perChat.current.set(key, slot);
    }

    // `items` is fresh every streaming event, so it is the dep but the VALUE is
    // the version - which only moves when an id is added.
    const version = useMemo(
        () => ingestChatSources(slot.index, {
            forEachResult,
            namespaceKey: key,
            items,
            seen: slot.seen,
        }),
        [slot, key, items],
    );

    return useMemo(
        () => makeSessionLinkifier({ index: slot.index, openScope }),
        [slot, openScope, version],
    );
}
